import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import MemberCard from './MemberCard';
import RedPandaDayBanner from './RedPandaDayBanner';
import { getAllMembers } from '../utils/memberLoader';
import { fadeInLeft, fadeInRight, staggerContainer, cardVariant } from '../utils/animations';

function Home() {
  const [featuredMembers, setFeaturedMembers] = useState([]);
  const [highlightedMember, setHighlightedMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showScrollArrow, setShowScrollArrow] = useState(true);
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const loadData = async () => {
      try {
        // Load all members
        const members = await getAllMembers();

        // Get featured members or first 3 if none are featured
        const featured =
          members.filter((member) => member.featured) || members.slice(0, 3);
        setFeaturedMembers(featured);

        // Get a random member for the highlight section
        const randomMember =
          members[Math.floor(Math.random() * members.length)];
        setHighlightedMember(randomMember);

        setLoading(false);
      } catch (error) {
        console.error('Error loading data for homepage:', error);
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Hide scroll arrow after 8 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowScrollArrow(false);
    }, 8000);

    return () => clearTimeout(timer);
  }, []);


  return (
    <>
      <Helmet>
        <title>Red Panda Treehouse - All Red Panda Vtuber Community</title>
        <meta name="description" content="Red Panda Treehouse is a vibrant, inclusive Vtuber community showcasing diverse content creators. Explore member profiles, discover featured highlights, and join our warm, welcoming space where creativity thrives in a supportive environment." />
        <meta name="keywords" content="red panda, vtuber, vtuber community, content creators, streaming, twitch, youtube, LGBTQ+ friendly, inclusive community, virtual youtuber" />
        <link rel="canonical" href="https://redpandatreehouse.com/" />

        {/* Open Graph */}
        <meta property="og:title" content="Red Panda Treehouse - All Red Panda Vtuber Community" />
        <meta property="og:description" content="Red Panda Treehouse is a vibrant, inclusive Vtuber community showcasing diverse content creators. Explore member profiles, discover featured highlights, and join our warm, welcoming space where creativity thrives in a supportive environment." />
        <meta property="og:url" content="https://redpandatreehouse.com/" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://redpandatreehouse.com/data/images/social-preview.png" />

        {/* Twitter */}
        <meta name="twitter:title" content="Red Panda Treehouse - All Red Panda Vtuber Community" />
        <meta name="twitter:description" content="A welcoming LGBTQ+ friendly Vtuber community featuring member profiles, highlights, and a supportive space for content creators." />
        <meta name="twitter:image" content="https://redpandatreehouse.com/data/images/social-preview.png" />

        {/* Structured Data - Organization Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Red Panda Treehouse",
            "description": "A vibrant, inclusive Vtuber community showcasing diverse content creators",
            "url": "https://redpandatreehouse.com",
            "logo": "https://redpandatreehouse.com/data/images/favicon.png",
            "image": "https://redpandatreehouse.com/data/images/social-preview.png",
            "sameAs": [
              "https://discord.gg/redpandatreehouse"
            ],
            "contactPoint": {
              "@type": "ContactPoint",
              "contactType": "Community Support"
            }
          })}
        </script>
      </Helmet>
      {/* <RedPandaDayBanner /> */}
      <section
        className='hero relative bg-cover bg-center overflow-hidden'
        style={{
          backgroundImage: 'url("data/images/HeroImage.png")',
          height: '95vh',
       
        }}
      >
        <div className='absolute inset-0 bg-gradient-to-br from-primary-dark to-secondary-dark opacity-70'></div>
        <div className='container mx-auto px-4 h-full flex flex-col justify-center items-center relative z-10'>
          <motion.h1
            initial="hidden"
            animate="visible"
            variants={fadeInLeft}
            className='text-5xl md:text-7xl font-bold mb-4 text-center text-primary-DEFAULT'
            style={{
              backgroundColor: 'rgba(255,255,255,0.8)',
              padding: '10px',
              borderRadius: '8px',
              textShadow: '0 2px 4px rgba(0,0,0,0.5), 0 0 10px rgba(0,0,0,0.3)'
            }}
          >
            Red Panda Treehouse
          </motion.h1>
          <motion.p
            initial="hidden"
            animate="visible"
            variants={fadeInRight}
            className='text-xl md:text-2xl text-center max-w-2xl mb-8 text-primary-DEFAULT'
            style={{
              textShadow: '0 1px 3px rgba(0,0,0,0.5), 0 0 8px rgba(0,0,0,0.2)',
              backgroundColor: 'rgba(255,255,255,0.8)',
              padding: '10px',
              borderRadius: '8px',
            }}
          >
            Join our welcoming community of LGBTQ+ friendly red panda VTubers
          </motion.p>
          <Link
            to='/members'
            className='bg-primary-DEFAULT hover:bg-primary-dark font-bold py-3 px-6 rounded-full transition-colors duration-300 text-primary-DEFAULT'
            style={{
              backgroundColor: 'rgba(255,255,255,0.8)',
              textShadow: '0px 1px 2px rgba(0,0,0,0.4)',
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
              border: '1px solid rgba(255,255,255,0.3)'
            }}
          >
            Meet Our Members
          </Link>
        </div>

        {/* Animated Scroll Arrow */}
        <AnimatePresence>
          {showScrollArrow && (
            <motion.div
            className="absolute bottom-24 left-1/2 transform -translate-x-1/2 text-white"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ delay: 1, duration: 0.8 }}
          >
            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              className="flex flex-col items-center cursor-pointer"
              onClick={() => {
                document.querySelector('.py-20').scrollIntoView({
                  behavior: 'smooth'
                });
                setShowScrollArrow(false);
              }}
            >
              <div className="text-lg font-medium mb-3 text-primary-50 drop-shadow-lg">
                Scroll Down
              </div>
              <svg
                className="w-10 h-10 drop-shadow-lg"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </motion.div>
          </motion.div>
          )}
        </AnimatePresence>

        {/* Artist Credit */}
        <div className="absolute bottom-2 right-2 text-xs text-white/70 bg-black/30 px-2 py-1 rounded backdrop-blur-sm z-20">
          Art by{' '}
          <a
            href="https://x.com/MiyamiVT"
            target="_blank"
            rel="noopener noreferrer"
            className="text-white/90 hover:text-white underline transition-colors duration-200"
          >
            @MiyamiVT
          </a>
        </div>
      </section>
      <section className='py-20 bg-gray-50 dark:bg-dark-DEFAULT'>
        <div className='container mx-auto px-4'>
          <h2 className='text-4xl font-bold text-center mb-16'>
            Featured Members
          </h2>
          {loading ? (
            <div className='text-center py-10'>
              <div className='inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-DEFAULT'></div>
              <p className='mt-4 text-lg'>Loading featured members...</p>
            </div>
          ) : (
            <div className='grid grid-cols-1 md:grid-cols-3 gap-8'>
              {featuredMembers.map((member, index) => (
                <motion.div 
                  key={member.id} 
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.3 }}
                  variants={cardVariant}
                  transition={{ 
                    duration: 0.6, 
                    delay: index * 0.15,
                    ease: "easeOut"
                  }}
                >
                  <MemberCard member={member} />
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className='py-20'>
        <div className='container mx-auto px-4'>
          <div className='flex flex-col md:flex-row items-center'>
            <div className='md:w-1/2 mb-8 md:mb-0'>
              <h2 className='text-4xl font-bold mb-6'>Member Highlight</h2>
              <p className='text-lg mb-6'>
                Check out our featured talent and their amazing content!
              </p>
              <Link
                to='/highlight'
                className='inline-block bg-primary-DEFAULT hover:bg-primary-dark font-bold py-2 px-6 rounded-full transition-colors duration-300'
              >
                See More
              </Link>
            </div>
            <div className='md:w-1/2'>
              {loading || !highlightedMember ? (
                <div
                  className='rounded-lg overflow-hidden shadow-xl bg-gray-200 dark:bg-dark-light flex items-center justify-center'
                  style={{ height: '300px' }}
                >
                  <div className='text-center'>
                    <div className='inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-DEFAULT'></div>
                    <p className='mt-4 text-lg'>Loading highlight...</p>
                  </div>
                </div>
              ) : (
                <motion.div 
                  className='rounded-lg overflow-hidden shadow-xl'
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.3 }}
                  variants={cardVariant}
                  transition={{ 
                    duration: 0.6,
                    ease: "easeOut"
                  }}
                >
                  <img
                    src={highlightedMember.image}
                    alt={highlightedMember.name}
                    className='w-full object-cover h-64'
                  />
                  <div className='p-4 bg-white dark:bg-dark-light'>
                    <h3 className='text-xl font-bold'>
                      {highlightedMember.name}
                    </h3>
                    <p className='text-primary-DEFAULT'>
                      {highlightedMember.title}
                    </p>
                  </div>
                </motion.div>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default Home;
